// useToggleMasterSwitch — the content pipeline's master switch
// (app_config `studio_agents_enabled`, kept under its historical key) through
// studio-toggle-master-switch, which checks admin and writes an audit row.
// Off: nothing is planned or finished and the pipeline's cron jobs no-op.

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '../queryKeys';

export interface ToggleMasterSwitchArgs {
  enabled: boolean;
}

export interface ToggleMasterSwitchResponse {
  ok: true;
  enabled: boolean;
}

export function useToggleMasterSwitch(): UseMutationResult<
  ToggleMasterSwitchResponse,
  Error,
  ToggleMasterSwitchArgs
> {
  const qc = useQueryClient();

  return useMutation<ToggleMasterSwitchResponse, Error, ToggleMasterSwitchArgs>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke<ToggleMasterSwitchResponse>(
        'studio-toggle-master-switch',
        { body: { enabled: input.enabled } },
      );
      if (error) throw error;
      if (!data) throw new Error('studio-toggle-master-switch returned no data');
      return data;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.appConfig.all });
    },
  });
}
